import { Directive, EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@xpo-ltl/data-api';
import { ListInfo } from '@xpo-ltl/sdk-common';
import {
  BulkReprocessIncentivesPath,
  BulkReprocessIncentivesRqst,
  BulkUpdateIncentiveAggregatePeriodsPath,
  BulkUpdateIncentiveAggregatePeriodsRqst,
  BulkUpdateIncentiveShipmentsPath,
  BulkUpdateIncentiveShipmentsRqst,
  BulkUpdateIncentiveStatusRqst,
  BulkUpdateProgramCustomerLocationsPath,
  BulkUpdateProgramCustomerLocationsRqst,
  CreateProgramCustomerLocationPath,
  CreateProgramCustomerLocationRqst,
  CustomerIncentiveLocation,
  DeleteIncentivePath,
  DeleteProgramCustomerLocationPath,
  GetIncentivePath,
  GetIncentiveResp,
  Incentive,
  IncentiveAggregatePeriod,
  IncentiveLocation,
  IncentiveProgram,
  IncentiveShipment,
  IncentiveWithBatchControl,
  ListCustomerIncentiveLocationsPath,
  ListCustomerIncentiveLocationsQuery,
  ListCustomerIncentiveLocationsResp,
  ListIncentiveBatchControlsResp,
  ListIncentiveShipmentsResp,
  ListIncentiveShipmentsRqst,
  ListIncentivesQuery,
  ListIncentivesResp,
  OffBillIncentiveApiService,
  OffBillIncentiveFilter,
  ProcessIncentiveResp,
  ProcessIncentiveRqst,
  UpdateIncentiveCommentResp,
  UpdateIncentiveCommentRqst,
  UpdateIncentiveLocationsPath,
  UpdateIncentiveLocationsRqst,
  UpdateIncentivePath,
  UpdateIncentiveProgramPath,
  UpdateIncentiveProgramRqst,
  UpdateIncentiveRqst,
} from '@xpo-ltl/sdk-offbillincentive';
import { ColumnState } from 'ag-grid-community';
import { map, Observable, shareReplay, Subject } from 'rxjs';
import { AgreementActionType } from '../../enums/agreement/agreementActionType.enum';
import { ObiQueryParams } from '../../enums/incentive/obi-query-params.enum';
import { AppRoutes } from '../../enums/app-routes.enum';
import { Util } from '../../enums/util.enum';
import { HelperService } from '../helper.service';
import { PricingWorkbenchService } from '../user-role/pricing-workbench.service';
import { IncentiveOwnerInfo } from '../../models/incentiveOwner.model';
import { NotificationMenuItem } from '../../models/notification-menu-item.model';
import { NotificationStateService } from '../notification-state.service';

@Directive()
@Injectable({
  providedIn: 'root',
})
export class ObiApiService {
  private destroySubjects = {
    list: new Subject<void>(),
    tabs: new Subject<void>(),
    detail: new Subject<void>(),
  };
  private obiIncentiveListCache$: Observable<Array<Incentive>>;
  incentiveOwnerList: IncentiveOwnerInfo[] = [
    {
      employeeId: 'TOB00070',
      employeeName: 'Accounts Payable Process',
    },
    {
      employeeId: 'OBIBATCH',
      employeeName: 'OBI Batch',
    },
  ];
  incentiveListQueryParams: ListIncentivesQuery = new ListIncentivesQuery();
  incentiveListQueryCache: ListIncentivesQuery;
  incentivePros: Partial<Incentive>;
  incentiveDiscount: number = 0;
  batchLength = 50;
  activeFilters = false;
  refreshDataSurce: EventEmitter<boolean> = new EventEmitter<boolean>();
  filters: ListIncentivesQuery;
  obiIncentiveId: number;
  obiIncentiveOwner: string;
  obiRunStatusCd: string;
  listInfo: ListInfo;

  protected columnState: ColumnState[];
  @Output() deleteIncentiveEmitter = new EventEmitter();
  constructor(
    public offBillIncentiveApiService: OffBillIncentiveApiService,
    private notificationStateService: NotificationStateService,
    private notificationService: NotificationService,
    private helperService: HelperService,
    private router: Router,
    private pricingWorkbenchService: PricingWorkbenchService
  ) {}

  deleteEmitter(obiIncentiveId) {
    this.deleteIncentiveEmitter.next(obiIncentiveId);
  }

  requestIncentiveList(incentiveListQueryParams?: ListIncentivesQuery): Observable<Incentive[]> {
    let incentiveListQueryCache: ListIncentivesQuery;
    if (incentiveListQueryParams) {
      incentiveListQueryCache = this.getIncentiveListQueryParams(incentiveListQueryParams);
    } else {
      this.incentiveListQueryCache = this.getIncentiveListQueryParams(this.incentiveListQueryParams);
      incentiveListQueryCache = this.incentiveListQueryCache;
    }
    return this.offBillIncentiveApiService.listIncentives(incentiveListQueryCache).pipe(
      map((incentiveListResp: ListIncentivesResp) => {
        const incentiveList = [];
        incentiveListResp.incentiveWithBatchControls.forEach(async (result: any) => {
          result.incentive.customerNameOnCheck = this.helperService.removeExtraWhiteSpaces(
            result.incentive.customerNameOnCheck
          );

          result.incentive.completedDate = result.incentive.auditInfo.updatedTimestamp;

          result.incentive.runStatusCd = result.incentiveBatchControl
            ? result.incentiveBatchControl.runStatusCd
            : 'SUCCESS';

          result.incentive.totalProsDiscountedNbr = result.incentive.totalProsDiscountedNbr
            ? result.incentive.totalProsDiscountedNbr
            : 0;
          result.incentive.totalProsPendingNbr = result.incentive.totalProsPendingNbr
            ? result.incentive.totalProsPendingNbr
            : 0;
          result.incentive.totalProsRejectedNbr = result.incentive.totalProsRejectedNbr
            ? result.incentive.totalProsRejectedNbr
            : 0;

          result.incentive.totalNotQualifiedProsCount = result.incentive.totalNotQualifiedProsCount
            ? result.incentive.totalNotQualifiedProsCount
            : 0;

          result.incentive.totalProsNbr =
            result.incentive.totalProsDiscountedNbr +
            result.incentive.totalProsPendingNbr +
            result.incentive.totalProsRejectedNbr +
            result.incentive.totalNotQualifiedProsCount;

          incentiveList.push(result.incentive);
        });

        return incentiveList;
      })
    );
  }

  requestIncentiveShipmentList(
    filter: OffBillIncentiveFilter[],
    listInfo: ListInfo
  ): Observable<ListIncentiveShipmentsResp> {
    const request = new ListIncentiveShipmentsRqst();
    request.incentiveShipmentFilters = filter;
    request.listInfo = listInfo;

    return this.offBillIncentiveApiService.listIncentiveShipments(request).pipe(
      map((resp: ListIncentiveShipmentsResp) => {
        resp.incentiveShipmentWithShipments.map((response: any) => {
          response.shipment.oracleCollectAmount = response.shipment.paymentAmount
            ? response.shipment.paymentAmount
            : '';
          response.shipment.collectAmount = response.shipment.netRevenueAmount
            ? response.shipment.netRevenueAmount
            : '';
          response.obiIncentiveShipmentId = response.incentiveShipment.obiIncentiveShipmentId;
          response.adjustmentAmount = response.incentiveShipment.adjustmentAmount;
          response.adjustedRate = response.incentiveShipment.adjustedRate;
          response.qualifiedInd = response.incentiveShipment.qualifiedInd;
          response.incentiveShipment.discountAmount =
            response.incentiveShipment.statusCd !== Util.rejectedCd ? response.incentiveShipment.discountAmount : 0;
        });
        return resp;
      })
    );
  }

  // async getIncentiveOwner(incentive: Incentive): Promise<string> {
  //   incentive.incentiveOwner = incentive.incentiveOwner
  //     ? incentive.incentiveOwner
  //     : incentive.auditInfo.updateById && incentive.statusCd != IncentiveStatusCd.PENDING.toUpperCase()
  //       ? incentive.auditInfo.updateById
  //       : incentive.auditInfo.updateByPgmId;

  //   let incentiveOwnerFound: IncentiveOwnerInfo = this.incentiveOwnerList.find((incentiveOwner) => {
  //     return incentiveOwner.employeeId === incentive.incentiveOwner;
  //   });

  //   if (
  //     !incentiveOwnerFound &&
  setIncentiveListQueryParams(params: ListIncentivesQuery): void {
    if (!params) this.incentiveListQueryParams = new ListIncentivesQuery();
    else this.incentiveListQueryParams = params;
    this.filters = params;
  }

  backToIncentiveList() {
    this.activeFilters = true;
    this.router.navigate([`${AppRoutes.OFF_BILL_INCENTIVE}/${AppRoutes.LIST_PAGE}`]);
  }

  getIncentiveListQueryParams(incentiveListQueryParams: ListIncentivesQuery): ListIncentivesQuery {
    incentiveListQueryParams.incentiveType =
      incentiveListQueryParams?.periodBeginDate ||
      incentiveListQueryParams?.periodEndDate ||
      incentiveListQueryParams?.customerName ||
      incentiveListQueryParams?.incentiveNbr ||
      incentiveListQueryParams?.proNbr ||
      incentiveListQueryParams?.madCd
        ? null
        : ObiQueryParams.CURRENT_PAY_PERIOD;
    return incentiveListQueryParams;
  }

  getIncentiveList(noValidate?: boolean): Observable<Incentive[]> {
    this.notificationService.hideOverlayMessage();
    if (!this.obiIncentiveListCache$ || this.incentiveListQueryCache !== this.incentiveListQueryParams || noValidate) {
      this.obiIncentiveListCache$ = this.requestIncentiveList().pipe(shareReplay());
    }
    return this.obiIncentiveListCache$;
  }

  setObiIncentiveId(id) {
    this.obiIncentiveId = id;
  }

  getObiIncentiveId() {
    return this.obiIncentiveId;
  }

  setObiIncentiveOwner(owner) {
    this.obiIncentiveOwner = owner;
  }

  setListInfo(listInfo) {
    this.listInfo = listInfo;
  }

  requestIncentiveDetail(obiIncentiveId): Observable<GetIncentiveResp> {
    const pathParams = new GetIncentivePath();
    if (obiIncentiveId) {
      pathParams.obiIncentiveId = obiIncentiveId;
    } else {
      pathParams.obiIncentiveId = this.obiIncentiveId;
    }
    return this.offBillIncentiveApiService.getIncentive(pathParams);
  }

  requestIncentiveLocation(obiIncentiveId, programId): Observable<CustomerIncentiveLocation[]> {
    const pathParams = new ListCustomerIncentiveLocationsPath();
    const queryParams = new ListCustomerIncentiveLocationsQuery();

    pathParams.obiIncentiveId = obiIncentiveId;
    queryParams.obiProgramId = programId;

    return this.offBillIncentiveApiService.listCustomerIncentiveLocations(pathParams, queryParams).pipe(
      map((response: ListCustomerIncentiveLocationsResp) => {
        response.customerIncentiveLocations.forEach((data: any) => {
          if (!data.acctId) data.acctId = {};
          data.acctId.acctName = data.acctId?.acctName
            ? this.helperService.removeExtraWhiteSpaces(data.acctId.acctName)
            : undefined;
          data.obiIncentiveLocationId = data.incentiveLocation.obiIncentiveLocationId;
          data.incentiveLocation.adjustmentAmount = !!data.incentiveLocation.adjustmentAmount
            ? data.incentiveLocation.adjustmentAmount
            : 0;
        });
        return response.customerIncentiveLocations;
      })
    );
  }

  updateAdjustmentBulk(incentiveLocations: IncentiveLocation[], incentiveId: number): Observable<void> {
    const request = new UpdateIncentiveLocationsRqst();
    request.incentiveLocations = incentiveLocations;
    const pathParams = new UpdateIncentiveLocationsPath();
    pathParams.obiIncentiveId = incentiveId;
    return this.offBillIncentiveApiService.updateIncentiveLocations(request, pathParams);
  }

  updateIncentiveShipments(actionType: string, incentiveShipments: IncentiveShipment[], incentiveId: number) {
    const request = new BulkUpdateIncentiveShipmentsRqst();
    request.actionType = actionType;
    request.incentiveShipments = incentiveShipments;
    const pathParams = new BulkUpdateIncentiveShipmentsPath();
    pathParams.obiIncentiveId = incentiveId;
    const dataOption = { toastOnError: false };
    return this.offBillIncentiveApiService.bulkUpdateIncentiveShipments(request, pathParams, dataOption);
  }

  updateIncentiveAggregatePeriods(aggregatePeriods: IncentiveAggregatePeriod[], programId) {
    const request = new BulkUpdateIncentiveAggregatePeriodsRqst();
    const pathParams = new BulkUpdateIncentiveAggregatePeriodsPath();

    request.incentiveAggregatePeriods = aggregatePeriods;
    pathParams.obiIncentiveProgramId = programId;

    return this.offBillIncentiveApiService.bulkUpdateIncentiveAggregatePeriods(request, pathParams);
  }

  deleteObiIncentive(obiIncentiveId: number) {
    const request = new DeleteIncentivePath();
    request.obiIncentiveId = obiIncentiveId;
    const dataOption = { toastOnError: false };
    return this.offBillIncentiveApiService.deleteIncentive(request, dataOption);
  }

  deleteObiProgramLocation(obiProgramLocationId, programId) {
    const request = new DeleteProgramCustomerLocationPath();
    request.obiProgramLocationId = obiProgramLocationId;
    request.obiProgramId = programId;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: true };
    return this.offBillIncentiveApiService.deleteProgramCustomerLocation(request, dataOption);
  }

  createObiProgramLocation(obiProgramLocation, obiProgramId) {
    const request = new CreateProgramCustomerLocationRqst();
    request.programCustomerLocation = obiProgramLocation;
    const pathParams = new CreateProgramCustomerLocationPath();
    pathParams.obiProgramId = obiProgramId;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: true };
    return this.offBillIncentiveApiService.createProgramCustomerLocation(request, pathParams, dataOption);
  }

  updateObiProgramLocation(
    obiProgramLocation,
    obiProgramId: number,
    type?: any,
    loadingOverlayEnabled: boolean = true
  ) {
    const request = new BulkUpdateProgramCustomerLocationsRqst();
    request.actionType = type ? type : AgreementActionType.CHANGE;
    if (obiProgramLocation.length) {
      request.programCustomerLocations = obiProgramLocation;
    } else {
      request.programCustomerLocations = [];
      request.programCustomerLocations.push(obiProgramLocation);
    }
    const pathParams = new BulkUpdateProgramCustomerLocationsPath();
    pathParams.obiProgramId = obiProgramId;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: loadingOverlayEnabled };
    return this.offBillIncentiveApiService.bulkUpdateProgramCustomerLocations(request, pathParams, dataOption);
  }

  updateIncentiveHeaderDetail(data: Incentive, obiIncentiveId: number, actionType: string) {
    const request = new UpdateIncentiveRqst();
    request.incentive = data;
    request.actionType = actionType;
    const pathParams = new UpdateIncentivePath();
    pathParams.obiIncentiveId = obiIncentiveId;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: true };
    return this.offBillIncentiveApiService.updateIncentive(request, pathParams, dataOption);
  }

  revertIncentives(obiIncentiveIds: number[], targetStatus: string) {
    const request = new BulkUpdateIncentiveStatusRqst();
    request.incentiveIds = obiIncentiveIds;
    request.targetStatus = targetStatus;
    return this.offBillIncentiveApiService.bulkUpdateIncentiveStatus(request);
  }

  updateIncentiveProgram(incentiveProgram: IncentiveProgram, obiIncentiveId: number) {
    const request = new UpdateIncentiveProgramRqst();
    request.incentiveProgram = incentiveProgram;
    request.obiIncentiveId = obiIncentiveId;
    const pathParams = new UpdateIncentiveProgramPath();
    pathParams.obiIncentiveProgramId = incentiveProgram.obiIncentiveProgramId;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: true };
    return this.offBillIncentiveApiService.updateIncentiveProgram(request, pathParams, dataOption);
  }

  updateIncentiveComment(data: Incentive): Observable<UpdateIncentiveCommentResp> {
    const request = new UpdateIncentiveCommentRqst();
    request.incentive = data;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: false };
    return this.offBillIncentiveApiService.updateIncentiveComment(request, dataOption);
  }

  createOffBillIncentive(data: ProcessIncentiveRqst): Observable<ProcessIncentiveResp> {
    const request = data;
    const dataOption = { toastOnError: false, loadingOverlayEnabled: true };
    return this.offBillIncentiveApiService.processIncentive(request, dataOption);
  }

  getBatchIncentives(): Observable<ListIncentiveBatchControlsResp> {
    const dataOption = { toastOnError: false, loadingOverlayEnabled: false };
    return this.offBillIncentiveApiService.listIncentiveBatchControls(dataOption);
  }

  getNotifications = () => {
    this.getBatchIncentives().subscribe((response: ListIncentiveBatchControlsResp) => {
      let notifications = [];
      response.incentiveWithBatchControls?.forEach((element: IncentiveWithBatchControl) => {
        notifications.push(
          new NotificationMenuItem(
            element.incentive.incentiveNbr.toString(),
            element.incentive.madCode,
            element.incentive.customerNameOnCheck,
            '-',
            element.incentiveBatchControl.runStatusCd === Util.failCd,
            element.incentive
          )
        );
      });
      this.notificationStateService.setNotifications(notifications);
    });
  };

  setIncentivePros(incentive: Incentive) {
    this.incentivePros = {
      totalProsDiscountedNbr: incentive.totalProsDiscountedNbr || 0,
      totalProsPendingNbr: incentive.totalProsPendingNbr || 0,
      totalProsRejectedNbr: incentive.totalProsRejectedNbr || 0,
      totalNotQualifiedProsCount: incentive.totalNotQualifiedProsCount || 0,
    };
  }

  setDiscountAmount(incentive: Incentive) {
    this.incentiveDiscount = incentive.discountAmount;
  }

  calculateTotalPros(incentivePros: Partial<Incentive>): number {
    return (
      (incentivePros.totalProsDiscountedNbr || 0) +
      (incentivePros.totalProsPendingNbr || 0) +
      (incentivePros.totalProsRejectedNbr || 0) +
      (incentivePros.totalNotQualifiedProsCount || 0)
    );
  }

  bulkReprocessIncentives(sourceIncentiveId: number, reprocessIncentiveIds: number[]) {
    const request = new BulkReprocessIncentivesRqst();
    request.reprocessIncentiveIds = reprocessIncentiveIds;
    const pathParams = new BulkReprocessIncentivesPath();
    pathParams.obiIncentiveId = sourceIncentiveId;
    return this.offBillIncentiveApiService.bulkReprocessIncentives(request, pathParams);
  }

  unsubscribeFromParent(name) {
    this.destroySubjects[name].next();
  }
}
